export type DeviceType =
  | 'switch'
  | 'router'
  | 'wifi_router'
  | 'access_point'
  | 'firewall'
  | 'server'
  | 'desktop'
  | 'laptop'
  | 'tablet'
  | 'display'
  | 'ip_phone'
  | 'wifi_phone'
  | 'printer'
  | 'camera'
  | 'nas'
  | 'modem'
  | 'lte_antenna'
  | 'satellite'
  | 'cloud'
  | 'iot'
  | 'ups'
  | 'power_station'
  | 'controller'
  | 'zone'
  | 'generic';

export type IconPackId =
  | 'ukrop'
  | 'classic_network'
  | 'flat_cloud'
  | 'minimal'
  | 'outline';

export type ConnectionType =
  | 'ethernet'
  | 'fiber'
  | 'wifi'
  | 'wan'
  | 'sip'
  | 'logical'
  | 'unknown';

/** Eth port placement on the device block. */
export type PortLayout = 'left' | 'right' | 'both' | 'all';

export type MonitorSource =
  | 'interface'
  | 'neighbor'
  | 'endpoint-derived'
  | 'manual'
  | 'unknown';

export type MonitorMode =
  | 'passive'
  | 'icmp'
  | 'tcp'
  | 'http'
  | 'dhcp_presence'
  | 'arp_presence'
  | 'wifi_registration'
  | 'router_interface'
  | 'neighbor'
  | 'derived'
  | 'manual';

export type Criticality = 'critical' | 'normal' | 'low';

export type ExpectedPresence = 'always' | 'business_hours' | 'optional';

export type NodeStatus =
  | 'online'
  | 'degraded'
  | 'offline'
  | 'inactive'
  | 'sleeping'
  | 'maintenance'
  | 'passive'
  | 'unknown'
  | 'stale'
  | 'site_unreachable';

export type LinkEvidence = 'measured' | 'derived' | 'unknown';

export type Role = 'viewer' | 'editor' | 'admin';

export interface DiagramPosition {
  x: number;
  y: number;
}

export interface DiagramSize {
  width: number;
  height: number;
}

/**
 * Local-only device inventory (browser / diagram JSON).
 * Never include these fields in cloud telemetry payloads.
 */
export interface DeviceInventory {
  /** Local MAC for identification / OUI vendor hint — not sent to Cloudflare */
  macLocal?: string;
  /** Local management / LAN IP — browser-only, not cloud telemetry */
  ipLocal?: string;
  /** Hardware model, e.g. RB2011UiAS, CRS326-24G-2S+ */
  model?: string;
  /** Vendor override; if empty, may be derived from MAC OUI */
  vendor?: string;
  location?: string;
  /** User photo (data URL) — preferred over catalog silhouette */
  photoDataUrl?: string;
  /** Cached silhouette after «Оновити малюнок» (from label/model) */
  appearanceId?: string;
}

export interface DiagramNode {
  id: string;
  monitorKey: string;
  type: DeviceType;
  label: string;
  description?: string;
  zoneId?: string | null;
  position: DiagramPosition;
  size?: DiagramSize;
  criticality: Criticality;
  monitorMode: MonitorMode;
  expectedPresence: ExpectedPresence;
  maintenanceUntil?: string | null;
  notes?: string;
  locked?: boolean;
  needsReview?: boolean;
  inventory?: DeviceInventory;
  /** Zone (or node) accent color: #RRGGBB */
  color?: string;
  /** Ethernet-style port count (0 = no Eth, e.g. Wi‑Fi-only phone). */
  portCount?: number;
  /**
   * Where Eth ports sit on the block.
   * Numbering starts at top-left and goes counter-clockwise.
   */
  portLayout?: PortLayout;
  /**
   * Named port layout preset (WAN/LAN/SFP…).
   * Prefer `interfaces` for new edits; kept for backward compatibility.
   */
  portProfileId?: string;
  /** Wi‑Fi antenna connectors (0 = none). Prefer interfaces.wifiCount. */
  wifiAntennaCount?: number;
  /**
   * Universal live/TTX interface setup (toggles + counts).
   * Used to build diagram ports regardless of exact vendor SKU.
   */
  interfaces?: DeviceInterfaces;
}

/** Flexible per-device interface map (from live gear / datasheet). */
export interface DeviceInterfaces {
  /** Dedicated WAN RJ45 (typically 1). */
  hasWan?: boolean;
  /** Copper Ethernet / LAN ports. */
  hasEthernet?: boolean;
  /** Number of Ethernet/LAN ports (WAN counted separately). */
  ethernetCount?: number;
  /** SFP / Mini-GBIC cages. */
  hasSfp?: boolean;
  /** SFP count 0–4. */
  sfpCount?: number;
  /** Wi‑Fi radios / antenna connectors on the diagram. */
  hasWifi?: boolean;
  wifiCount?: number;
  /** 2.4 GHz radio present. */
  wifi24?: boolean;
  /** 5 GHz / 5.8 GHz radio present. */
  wifi58?: boolean;
}

export interface DiagramEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourcePort?: string;
  targetPort?: string;
  connectionType: ConnectionType;
  monitorSource: MonitorSource;
  monitorKey?: string;
  label?: string;
  criticality: Criticality;
  notes?: string;
  needsReview?: boolean;
  /** Connection stroke color: #RRGGBB (muted palette preferred) */
  color?: string;
  /** Label position along the edge path, 0..1 (default ~0.5). */
  labelT?: number;
  /** Manual corridor shift in px (drag label off-axis to bend the route). */
  routeOffset?: number;
  /**
   * Local IPv4/IPv6 network on this link (CIDR), e.g. 192.168.1.0/24.
   * Browser-only — not sent in cloud telemetry.
   */
  networkCidr?: string;
}

export type NetworkLifecycle =
  | 'draft'
  | 'deployment'
  | 'modernization'
  | 'reconfiguration'
  | 'testing'
  | 'production'
  | 'maintenance'
  | 'archive';

/** Stable order for UI dropdowns. */
export const NETWORK_LIFECYCLE_ORDER: NetworkLifecycle[] = [
  'draft',
  'deployment',
  'modernization',
  'reconfiguration',
  'testing',
  'production',
  'maintenance',
  'archive',
];

/** Short labels for the app UI (ribbon, badges). */
export const NETWORK_LIFECYCLE_LABELS_UK: Record<NetworkLifecycle, string> = {
  draft: 'Проектування (Чернетка)',
  deployment: 'Розгортання (Монтаж)',
  modernization: 'Модернізація (Частково працює)',
  reconfiguration: 'Реконфігурація (Live-зміни)',
  testing: 'Тестування (Аудит)',
  production: 'В експлуатації (Стабільна)',
  maintenance: 'Технічне обслуговування',
  archive: 'Архів (Замінено)',
};

/** Full explanations for drawings and technical documentation only. */
export const NETWORK_LIFECYCLE_DESCRIPTIONS_UK: Record<NetworkLifecycle, string> = {
  draft:
    'Мережа існує лише у вигляді схеми на екрані, залізо ще не підключене.',
  deployment:
    'Триває фізичне прокладання кабелів та встановлення обладнання, мережа ще не запущена.',
  modernization:
    'Частина мережі вже запущена та моніториться, але архітектура активно змінюється або доопрацьовується.',
  reconfiguration:
    'Мережа повністю робоча, але адміністратор прямо зараз вносить правки в налаштування, змінює VLAN чи переключає порти.',
  testing:
    'Усі елементи підключені, триває перевірка швидкості, стабільності та пошук помилок перед фінальним здаванням.',
  production:
    'Мережа працює у штатному режимі, зміни не плануються, увімкнено повний моніторинг.',
  maintenance:
    'Тимчасовий статус для проведення планових ремонтних робіт (можливе падіння хостів).',
  archive:
    'Стара схема мережі, яка збережена для історії після глобальної перебудови.',
};

/** Map legacy / unknown values to the current lifecycle set. */
export function normalizeNetworkLifecycle(
  raw: string | null | undefined,
): NetworkLifecycle {
  switch (raw) {
    case 'draft':
    case 'planned':
    case 'design':
      return 'draft';
    case 'deployment':
      return 'deployment';
    case 'modernization':
      return 'modernization';
    case 'reconfiguration':
      return 'reconfiguration';
    case 'testing':
      return 'testing';
    case 'production':
    case 'live':
      return 'production';
    case 'maintenance':
      return 'maintenance';
    case 'archive':
      return 'archive';
    default:
      return 'draft';
  }
}

export function formatLifecycleForDocument(raw?: string | null): {
  id: NetworkLifecycle;
  label: string;
  description: string;
} {
  const id = normalizeNetworkLifecycle(raw);
  return {
    id,
    label: NETWORK_LIFECYCLE_LABELS_UK[id],
    description: NETWORK_LIFECYCLE_DESCRIPTIONS_UK[id],
  };
}

export interface DiagramDocument {
  schemaVersion: 1;
  id: string;
  siteId: string;
  name: string;
  updatedAt: string;
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  viewport?: { x: number; y: number; zoom: number };
  /** Project stage (short code). Full description is for docs/drawings only. */
  lifecycle?: NetworkLifecycle;
}

export interface TelemetrySystem {
  cpuLoad: number;
  freeMemoryBytes: number;
  totalMemoryBytes?: number;
  freeStorageBytes: number;
  routerOsVersion: string;
  boardName?: string;
  temperatureC?: number;
  voltageV?: number;
}

export interface TelemetryWanLink {
  monitorKey: string;
  status: 'online' | 'offline' | 'standby' | 'unknown';
}

export interface TelemetryWan {
  active: string;
  links: TelemetryWanLink[];
}

export interface TelemetryInterface {
  monitorKey: string;
  running: boolean;
  disabled: boolean;
  rxBytes: number;
  txBytes: number;
  rxPackets?: number;
  txPackets?: number;
  rxErrors?: number;
  txErrors?: number;
  rxDrops?: number;
  txDrops?: number;
  linkDowns?: number;
  speedMbps?: number;
  duplex?: string;
}

export interface TelemetryTarget {
  monitorKey: string;
  status: 'online' | 'degraded' | 'offline' | 'inactive' | 'sleeping' | 'unknown';
  latencyMs?: number | null;
  packetLossPercent?: number | null;
  changedAt?: string;
}

export interface TelemetryPayload {
  schemaVersion: 1;
  siteId: string;
  routerId: string;
  sequence: number;
  routerTime: string;
  uptimeSeconds: number;
  system: TelemetrySystem;
  wan: TelemetryWan;
  interfaces: TelemetryInterface[];
  targets: TelemetryTarget[];
}

export interface ResolvedNodeState {
  monitorKey: string;
  status: NodeStatus;
  evidence: string;
  updatedAt?: string;
}

export interface ResolvedLinkState {
  edgeId: string;
  status: NodeStatus;
  evidence: LinkEvidence;
  monitorSource: MonitorSource;
}

export const DEVICE_TYPE_LABELS_UK: Record<DeviceType, string> = {
  switch: 'Комутатор',
  router: 'Маршрутизатор',
  wifi_router: 'Wi-Fi маршрутизатор',
  access_point: 'Точка доступу',
  firewall: 'Мережевий екран',
  server: 'Сервер',
  desktop: 'ПК / робоча станція',
  laptop: 'Ноутбук',
  tablet: 'Планшет',
  display: 'Телевізор / монітор',
  ip_phone: 'IP-телефон',
  wifi_phone: 'Wi-Fi телефон / смартфон',
  printer: 'Принтер / МФУ',
  camera: 'IP-камера',
  nas: 'NAS / сховище',
  modem: 'Модем / ONT / термінал',
  lte_antenna: 'LTE антена / тарілка',
  satellite: 'Супутникова антена',
  cloud: 'Хмара / інтернет',
  iot: 'IoT / сенсор',
  ups: 'ДБЖ (мережевий)',
  power_station: 'Зарядна станція / портативний ДБЖ',
  controller: 'Контролер / модуль',
  zone: 'Зона',
  generic: 'Універсальний пристрій',
};

export const ICON_PACK_LABELS_UK: Record<IconPackId, string> = {
  ukrop: 'UKROP (базовий)',
  classic_network: 'Класична мережа (Cisco-like)',
  flat_cloud: 'Плоский хмарний (Microsoft-like)',
  minimal: 'Мінімалізм (Apple-like)',
  outline: 'Контурний (Lucide)',
};

export const ICON_PACK_NOTES_UK: Record<IconPackId, string> = {
  ukrop: 'Власні SVG UKROP.',
  classic_network:
    'Власний геометричний стиль, натхненний класичними мережевими схемами. НЕ копія іконок Cisco.',
  flat_cloud:
    'Власний flat-стиль. НЕ офіційні Azure/Microsoft Architecture Icons (їх можна підключити окремо за умовами Microsoft).',
  minimal: 'Власний мінімалістичний стиль. НЕ SF Symbols / Apple icons.',
  outline: 'Набір на базі Lucide (ISC) + власні доповнення для мережевих типів.',
};

export const STATUS_LABELS_UK: Record<NodeStatus, string> = {
  online: 'Онлайн',
  degraded: 'Погіршено',
  offline: 'Офлайн',
  inactive: 'Неактивний',
  sleeping: 'Сон',
  maintenance: 'Обслуговування',
  passive: 'Пасивний',
  unknown: 'Невідомо',
  stale: 'Застарілі дані',
  site_unreachable: 'Обʼєкт недоступний',
};

export const CRITICALITY_LABELS_UK: Record<Criticality, string> = {
  critical: 'Критична',
  normal: 'Звичайна',
  low: 'Низька',
};

export const STATUS_COLORS: Record<NodeStatus, string> = {
  online: '#1f9d55',
  degraded: '#d69e2e',
  offline: '#e53e3e',
  inactive: '#4a6fa5',
  sleeping: '#6b5b95',
  maintenance: '#3182ce',
  passive: '#cbd5e0',
  unknown: '#a0aec0',
  stale: '#dd6b20',
  site_unreachable: '#9b2c2c',
};

export const FORBIDDEN_TELEMETRY_FIELDS = [
  'ip',
  'mac',
  'serial',
  'password',
  'community',
  'configuration',
] as const;
